/** Escala alineada a Poblaciones — Densidad de población — Radios 2022 (hab/km²). */
export const POPULATION_DENSITY_LEGEND = [
  { key: 'sin_dato', label: 'Sin dato', color: '#BDBDBD' },
  { key: 'lt10', label: 'Menor que 10', color: '#313695' },
  { key: '10_50', label: '10 a 50', color: '#4575B4' },
  { key: '50_1000', label: '50 a 1.000', color: '#74ADD1' },
  { key: '1000_2000', label: '1.000 a 2.000', color: '#ABD9E9' },
  { key: '2000_3500', label: '2.000 a 3.500', color: '#C7E9B4' },
  { key: '3500_5000', label: '3.500 a 5.000', color: '#FEE08B' },
  { key: '5000_7000', label: '5.000 a 7.000', color: '#FDAE61' },
  { key: '7000_10000', label: '7.000 a 10.000', color: '#F46D43' },
  { key: '10000_15000', label: '10.000 a 15.000', color: '#D73027' },
  { key: '15000_plus', label: '15.000 y más', color: '#A50026' },
];

const MISSING_VALUE_COLOR = 'rgba(255,255,255,0.38)';

const bucket = (key, label, color) => ({ key, label, color });

/** Parsea `population_density` (hab/km²). null = ausente en API. */
export const parsePopulationDensity = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = typeof value === 'number' ? value : Number(String(value).trim().replace(',', '.'));
  if (!Number.isFinite(n) || Number.isNaN(n)) return null;
  return Math.max(0, n);
};

/** Bucket según rangos oficiales del visor Poblaciones (Radios 2022). */
export const classifyPopulationDensity = (density) => {
  if (density == null || Number.isNaN(density)) {
    return bucket('sin_dato', 'Sin dato', '#BDBDBD');
  }

  const d = Number(density);
  if (d < 10) return bucket('lt10', 'Menor que 10', '#313695');
  if (d < 50) return bucket('10_50', '10 a 50', '#4575B4');
  if (d < 1000) return bucket('50_1000', '50 a 1.000', '#74ADD1');
  if (d < 2000) return bucket('1000_2000', '1.000 a 2.000', '#ABD9E9');
  if (d < 3500) return bucket('2000_3500', '2.000 a 3.500', '#C7E9B4');
  if (d < 5000) return bucket('3500_5000', '3.500 a 5.000', '#FEE08B');
  if (d < 7000) return bucket('5000_7000', '5.000 a 7.000', '#FDAE61');
  if (d < 10000) return bucket('7000_10000', '7.000 a 10.000', '#F46D43');
  if (d < 15000) return bucket('10000_15000', '10.000 a 15.000', '#D73027');
  return bucket('15000_plus', '15.000 y más', '#A50026');
};

/** Formato del valor numérico para la ficha del lote. */
export const formatPopulationDensityValue = (density) => {
  const d = Number(density);
  if (!Number.isFinite(d)) return '—';

  if (d < 1000) {
    return `${new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(d)} hab/km²`;
  }

  return `${new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(d))} hab/km²`;
};

export const buildPopulationDensityTooltipMessage = ({
  valueLabel,
  rangeLabel = null,
  isMissing = false,
}) => {
  if (isMissing) {
    return 'No hay datos de densidad poblacional disponibles para el radio censal de este lote.';
  }

  const base = `El lote se ubica en un radio censal con una densidad de ${valueLabel}.`;
  if (rangeLabel) return `${base} Rango: ${rangeLabel}.`;
  return base;
};

/**
 * Presentación de densidad poblacional para el panel del lote.
 * Campo API: `population_density` (hab/km²).
 */
export const getPopulationDensityPanelDisplay = (value) => {
  const parsed = parsePopulationDensity(value);
  const isMissing = parsed === null;
  const bucketInfo = isMissing
    ? classifyPopulationDensity(null)
    : classifyPopulationDensity(parsed);

  const valueLabel = isMissing ? 'Sin dato' : formatPopulationDensityValue(parsed);
  const rangeLabel = isMissing ? null : `${bucketInfo.label} / km²`;

  const accentColor = isMissing
    ? MISSING_VALUE_COLOR
    : bucketInfo.color;

  return {
    status: 'ready',
    isMissing,
    density: parsed,
    valueLabel,
    rangeLabel,
    accentColor,
    tooltip: buildPopulationDensityTooltipMessage({
      valueLabel,
      rangeLabel,
      isMissing,
    }),
  };
};
